import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const StreamingTechnology: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/blog')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('blog.backToBlog')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          Industry Insights • August 11, 2026
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          Video Streaming Technology Explained
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          You click play. A 4K video starts instantly, no buffering, even on 4G. Behind this seamless experience is complex engineering—adaptive bitrates, CDNs, codecs, and edge computing. This guide demystifies video streaming technology, explaining how platforms like IndeeClips, YouTube, and Netflix deliver billions of hours of video without breaking the internet.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          The Basics: How Streaming Works
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Streaming vs Downloading
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Download:</strong> Entire file transferred before playback (used in the early 2000s—slow, storage-heavy)</li>
          <li><strong>Progressive download:</strong> Video plays while downloading (better, but you still wait)</li>
          <li><strong>Streaming (modern):</strong> Video split into tiny chunks, sent on-demand as you watch. No need to download entire file.</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          The Streaming Process (Simplified)
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>You click play</strong> on a video</li>
          <li><strong>Your device requests</strong> the video from the server</li>
          <li><strong>Server sends first chunk</strong> (usually 4-6 seconds of video)</li>
          <li><strong>Your device buffers</strong> next chunks while playing current one</li>
          <li><strong>Process repeats</strong> until video ends or you stop watching</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Result: instant playback, minimal buffering, no huge file stored on your device.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Adaptive Bitrate Streaming (ABR)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          The secret sauce behind smooth streaming on slow connections.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          The Problem
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Internet speed fluctuates. You start watching on WiFi (fast), move to 4G (slower), then to a congested network (very slow). A fixed-quality stream would buffer constantly.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          The Solution: Multiple Quality Versions
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          When you upload to IndeeClips, we create multiple versions of your video:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>1080p HD:</strong> ~5 Mbps bitrate (high quality, needs fast internet)</li>
          <li><strong>720p HD:</strong> ~2.5 Mbps</li>
          <li><strong>480p SD:</strong> ~1 Mbps</li>
          <li><strong>360p:</strong> ~0.5 Mbps (works on slow 3G)</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Your video player monitors your internet speed in real-time. Slow connection? Switches to 360p. Speed improves? Ramps back to 720p or 1080p. All automatic, no user input needed.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Video Codecs: Compression Magic
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          What Is a Codec?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Raw video is massive. 1 minute of uncompressed 1080p video = ~3GB. A codec (coder-decoder) compresses video to manageable size without destroying quality.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Common Codecs
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>H.264 (AVC):</strong> Industry standard since 2003. Good compression, universal playback support. Most YouTube/IndeeClips videos use this.</li>
          <li><strong>H.265 (HEVC):</strong> 50% better compression than H.264, but slower encoding and licensing costs. Used for 4K content.</li>
          <li><strong>VP9:</strong> Google's open-source codec. Free, good compression, used on YouTube.</li>
          <li><strong>AV1:</strong> Next-gen open codec. 30% better than VP9, but computationally expensive. Slow adoption (2026), will dominate by 2030.</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          IndeeClips uses H.264 for most content (universal compatibility) and H.265 for 4K videos.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Content Delivery Networks (CDNs)
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          The Problem: Distance and Latency
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          If all IndeeClips videos served from one server in Mumbai, a user in Kashmir would experience high latency (delay) and slow speeds—data has to travel 2000+ km.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          The Solution: Edge Servers
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          CDNs place copies of popular videos on servers worldwide. When you watch a video:
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Your device requests video</strong></li>
          <li><strong>CDN finds nearest edge server</strong> (e.g., Delhi server for Delhi user)</li>
          <li><strong>Video streams from nearby server</strong> instead of distant origin</li>
          <li><strong>Result:</strong> Lower latency, faster load times, less buffering</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          IndeeClips uses CDN nodes in Mumbai, Delhi, Bangalore, Hyderabad, Chennai, and Kolkata. Your video loads from whichever is closest.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Streaming Protocols
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          HLS (HTTP Live Streaming)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Apple's protocol, now industry standard. Works on all devices. Video split into .ts chunks (4-6 sec each) with a .m3u8 playlist file. Your player reads the playlist and fetches chunks sequentially.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          DASH (Dynamic Adaptive Streaming over HTTP)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Open alternative to HLS. Similar concept—chunk-based delivery, adaptive bitrate. Used by YouTube and Netflix.
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          IndeeClips supports both HLS and DASH for maximum compatibility.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Live Streaming Technology
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Live streaming is harder than on-demand—video must be encoded, delivered, and played in real-time (under 3-second delay).
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          How Live Streaming Works
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Creator captures video</strong> (camera/screen)</li>
          <li><strong>Encoder compresses</strong> in real-time (H.264 typically)</li>
          <li><strong>Sent to ingest server</strong> via RTMP protocol</li>
          <li><strong>Server transcodes to multiple qualities</strong> (1080p, 720p, 480p)</li>
          <li><strong>Distributed via CDN</strong> to viewers worldwide</li>
          <li><strong>Viewers watch</strong> with 2-5 second latency</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Challenges of Live Streaming
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Latency:</strong> Balancing low delay with stream stability—ultra-low latency ({'<'} 1 sec) risks buffering</li>
          <li><strong>Scalability:</strong> 100K simultaneous viewers = massive bandwidth demand</li>
          <li><strong>Network instability:</strong> Creator's upload speed fluctuates—stream quality drops or disconnects</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          The Indian Internet Challenge
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          India has unique streaming challenges:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Variable speeds:</strong> 3G, 4G, WiFi—users switch networks mid-video</li>
          <li><strong>Data costs:</strong> Many users on limited data plans—need aggressive compression</li>
          <li><strong>Device diversity:</strong> ₹5K budget phones to ₹1L flagships—codec support varies</li>
          <li><strong>Network congestion:</strong> Peak hours (8-10 PM) slow speeds dramatically</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          IndeeClips Optimizations for India
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Aggressive bitrate optimization:</strong> 360p streams work smoothly on 2G speeds</li>
          <li><strong>India-based CDN:</strong> Faster than routing through Singapore/US servers</li>
          <li><strong>H.264 priority:</strong> Universal device support over newer codecs</li>
          <li><strong>Smart buffering:</strong> Pre-load more content on WiFi, less on mobile data</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          The Future of Streaming Tech
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          AV1 Codec Adoption
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          By 2028, AV1 will replace H.264 as the standard. Same quality at 30% smaller file size means faster loading and lower data usage—critical for India.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Edge Computing
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Instead of centralized servers, processing moves to the edge (closer to users). Real-time transcoding at edge servers reduces latency and bandwidth.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          AI-Powered Compression
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Machine learning models compress video more efficiently than traditional codecs. Google and Meta are researching AI codecs that could halve file sizes by 2030.
        </Typography>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Understanding the Tech Makes You a Better Creator
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
            Knowing how streaming works helps you optimize uploads—choosing the right resolution, bitrate, and format. While IndeeClips handles the heavy lifting, creators who understand codecs, bitrates, and CDNs make better technical decisions. The magic isn't magic—it's engineering built to deliver billions of hours of video seamlessly.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Questions about video technology? Email{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
            .
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default StreamingTechnology;
